import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, TextField} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PropTypes from 'prop-types';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {InlineWidget} from 'react-calendly';

const AppointmentBooking = ({
                                index,
                                componentId,
                                descriptionHtml,
                                onDescriptionHtmlChange,
                                commitDescriptionHtml,
                                bookingLink = '',
                                onChange,
                                isEditMode,
                                isFormArchived,
                                placeholderValues,
                                readOnly,
                                accessLevel,
                                audience,
                                recipientType,
                                linkedToStudents,
                                submitMode
                            }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [iframeSrc, setIframeSrc] = useState(null);

    const CALENDLY_REGEX = /^https:\/\/calendly\.com\/[\w-]+/;
    const GOOGLE_SCHEDULER_REGEX = /^https:\/\/calendar\.google\.com\/calendar\/.+$/;

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const isValidBookingLink = (url) => {
        const trimmedUrl = url.trim();
        return CALENDLY_REGEX.test(trimmedUrl) || GOOGLE_SCHEDULER_REGEX.test(trimmedUrl);
    };

    const isCalendlyLink = (url) => CALENDLY_REGEX.test(url.trim());

    const validateUrl = (url) => {
        const trimmedUrl = url.trim();

        if (!trimmedUrl) {
            setErrorText('');
            return false;
        }

        if (!trimmedUrl.startsWith('https://')) {
            setErrorText('URL must start with https://');
            return false;
        }

        if (!isValidBookingLink(trimmedUrl)) {
            setErrorText(
                'Only full Google Calendar scheduler links (e.g. https://calendar.google.com/...) or Calendly links are accepted.'
            );
            return false;
        }

        setErrorText('');
        return true;
    };

    const handleUrlChange = (e) => {
        const newUrl = e.target.value;

        validateUrl(newUrl);

        if (onChange) {
            onChange({
                index,
                bookingLink: newUrl,
            });
        }

        if (!newUrl) {
            setIframeSrc(null);
        }
    };

    const openIframe = () => {
        if (!errorText && bookingLink) {
            setIframeSrc(bookingLink);
        }
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <RichEditor
                        currentDescription={draftDescriptionHtml}
                        setCurrentDescription={handleDraftChange}
                        isFormLive={isFormArchived}
                        accessLevel={accessLevel}
                        sx={{mb: 2}}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        placeholderValues={placeholderValues}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                )
                : (
                    <RichEditorResultText
                        id={componentId}
                        description={descriptionHtml}
                        placeholderValues={placeholderValues}
                        readonly={readOnly}
                        enableValidation={false}
                        sx={{mb: 2}}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        submitMode={submitMode}
                    />
                )}

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1
            }}>
                {isEditMode && (
                    <TextField
                        placeholder="Enter calendar booking link here"
                        value={bookingLink}
                        onChange={handleUrlChange}
                        error={!!errorText}
                        helperText={errorText}
                        sx={{
                            flexGrow: 1,
                            minWidth: '200px',
                            pr: 1,
                            py: 1
                        }}
                    />
                )}
                <Button
                    onClick={openIframe}
                    startIcon={<CalendarMonthIcon/>}
                    variant="outlined"
                    disabled={!!errorText || !bookingLink || readOnly}
                    sx={{
                        minWidth: '200px',
                        whiteSpace: 'nowrap'
                    }}
                >
                    Book Appointment
                </Button>
            </Box>

            {iframeSrc &&
                (isCalendlyLink(iframeSrc)
                    ? (
                        <Box
                            sx={{
                                mt: 2,
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}
                        >
                            <InlineWidget
                                url={`${iframeSrc}?hide_gdpr_banner=1`}
                                styles={{
                                    minWidth: '320px',
                                    height: '700px'
                                }}
                            />
                        </Box>
                    )
                    : (
                        <Box
                            sx={{
                                mt: 2,
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}
                        >
                            <iframe
                                src={iframeSrc}
                                width="100%"
                                height="auto"
                                style={{
                                    border: 'none',
                                    minHeight: '700px'
                                }}
                                title="Appointment Booking"
                                sandbox="allow-scripts allow-same-origin allow-forms"
                            />
                        </Box>
                    ))}
        </Box>
    );
};

AppointmentBooking.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    bookingLink: PropTypes.string,
    index: PropTypes.number,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default AppointmentBooking;
